import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import CryptoJS from "crypto-js";
import * as amplitude from "@amplitude/analytics-browser";

import { useConsoleLog } from "../../hooks/consoleLog";

import { useAppConfig } from "../../context/AppConfig/AppConfigContext";
import { useEnvContext } from "../../context/EnvironmentContext";
import { useDevMode } from "../../context/DevMode";

import { useEventFilter } from "./EventFilter";
import { getCheckoutEventProduct } from "./checkoutEventProduct";

// TODO update this logic
import { useUserData } from "../../context/UserData/UserDataContext";
import { useUrlContext } from "../../context/urlContext";

import {
  OneSignalEventName,
  reportOneSignalEvent,
} from "./OneSignal/reportEvent";
import { getCurrentPageUrl } from "../../utils/pageUrl";


const EventContext = createContext();

export const useEvent = () => useContext(EventContext);

export const EventProvider = ({ children }) => {
  const { newHpath, newT2path, isGoogleAdsTraffic, isMetaAdsTraffic } = useUrlContext();
  const { stagingEventsStatus } = useDevMode();
  const { trigerUserAnalytics, setTrigerUserAnalytics, state } = useUserData();

  const { snaptrEventStatus, metaEventStatus } = useEventFilter();

  const {
    appData,
    apiGeoUrl,
    emailsToDisableAnalyticsEvent,
    analyticsData: {
      currency: currencyType,
      meta_event_status: metaEventConfigStatus = true,
      snaptr_event_status: snaptrEventConfigStatus = true,
      google_event_status: googleEventConfigStatus = true,
      amplitude_event_status: amplitudeEventConfigStatus = true,
      tiktok_event_status: tiktokEventConfigStatus = true,
      axon_event_status: axonEventConfigStatus = true,
      dressx_event_status: dressxEventConfigStatus = true,
    } = {},
  } = useAppConfig();

  const { isProduction } = useEnvContext();

  const logger = useConsoleLog();

  const [userData, setSnaptrUserData] = useState({
    userEmail: state.email || null,
    userName: state.name || null,
  });

  useEffect(() => {
    setSnaptrUserData({
      userEmail: state.email || null,
      userName: state.name || null,
    });
  }, [state.email, state.name]);

  const [userGeoData, setUserGeoData] = useState(
    JSON.parse(localStorage.getItem("MemoryOSGeoData"))
  );

  const userIdentifyEvent = async (eventName, eventProperty) => {
    const identifyEvent = new amplitude.Identify();

    logger.event(
      ` Amplitude userIdentifyEvent`,
      `property: ${eventName}, value: ${eventProperty}`
    );

    identifyEvent.set(eventName.toString(), eventProperty.toString());

    amplitude.identify(identifyEvent);

  };

  const hashData = (data) => {
    const lowercaseData = data.toLowerCase();
    const hashedData = CryptoJS.SHA256(lowercaseData).toString();
    return hashedData;
  };

  const getEmailFromUserState = (emailsToDisableAnalyticsEvent) => {
    const quizData = JSON.parse(localStorage.getItem("MemoryOSQuizData"));

    const findEmailInQuizData = (data) => {
      if (newHpath) {
        const emailItem = data.find((item) => item.stepType === "email");
        return emailItem?.answer ?? null;
      } else {
        const formItem = data.find(
          (item) =>
            item.type === "form" && item.formValues && item.formValues.Email
        );
        return formItem ? formItem.formValues.Email : null;
      }

    };

    const checkEmailInDisableList = (email) => {
      return !emailsToDisableAnalyticsEvent.includes(email.toLowerCase());
    };

    if (state.email) {
      return checkEmailInDisableList(state.email) ? true : false;
    }

    if (quizData) {
      const quizEmail = findEmailInQuizData(quizData);
      if (quizEmail) {
        return checkEmailInDisableList(quizEmail) ? true : false;
      }
    }

    return true;
  };

  const [isAnalyticsAllowed, setIsAnalyticsAllowed] = useState(null);

  const checkAnalyticsConditions = useCallback(() => {
    return (
      isProduction &&
      appData.REACT_APP_MARKETING_TELEMETRY !== 0 &&
      getEmailFromUserState(emailsToDisableAnalyticsEvent)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isProduction,
    appData.REACT_APP_MARKETING_TELEMETRY,
    emailsToDisableAnalyticsEvent,
    state.email,
  ]);

  useEffect(() => {
    const updateAnalyticsAllowed = () => {
      const allowed = checkAnalyticsConditions();
      setIsAnalyticsAllowed(allowed);
    };
    updateAnalyticsAllowed();

    const interval = setInterval(updateAnalyticsAllowed, 60000);
    return () => clearInterval(interval);
  }, [checkAnalyticsConditions]);

  const canTrackMetaEvents = useCallback(() => {
    return (
      typeof window.fbq === "function" &&
      metaEventStatus === true &&
      metaEventConfigStatus === true
    );
  }, [metaEventStatus, metaEventConfigStatus]);

  const canTrackSnaptrEvents = useCallback(() => {
    return (
      typeof window.snaptr === "function" &&
      snaptrEventStatus === true &&
      snaptrEventConfigStatus === true
    );
  }, [snaptrEventStatus, snaptrEventConfigStatus]);

  const canTrackGoogleEvents = useCallback(() => {
    return (
      typeof window.gtag === "function" &&
      googleEventConfigStatus === true
    );
  }, [googleEventConfigStatus]);

  const canUseAmplitudeEvents = useCallback(() => {
    return (
      typeof amplitude !== "undefined" &&
      amplitudeEventConfigStatus === true
    );
  }, [amplitudeEventConfigStatus]);

  const canTrackAmplitudeEvents = useCallback(() => {
    return (
      canUseAmplitudeEvents() &&
      typeof amplitude.track === "function"
    );
  }, [canUseAmplitudeEvents]);

  const canTrackTikTokEvents = useCallback(() => {
    return (
      typeof window.ttq?.track === "function" &&
      tiktokEventConfigStatus === true
    );
  }, [tiktokEventConfigStatus]);

  const canTrackAxonEvents = useCallback(() => {
    return (
      typeof window.axon === "function" &&
      axonEventConfigStatus === true
    );
  }, [axonEventConfigStatus]);

  const canTrackDressxEvents = useCallback(() => {
    return (
      typeof window.dxq === "function" &&
      dressxEventConfigStatus === true
    );
  }, [dressxEventConfigStatus]);

  const getAnalyticsParams = () => {
    const { email, name, cid } = state;

    const user_cid = cid;
    const leadParams = { email, name };
    const completeRegistrationParams = { email, name, cid };
    return { user_cid, leadParams, completeRegistrationParams };
  };

  const analyticsEvent = useCallback(
    (eventName, eventParams) => {

      // TODO create better solution to verify events sender
      // if (!isProduction && !stagingEventsStatus) {
      //   if (!isAnalyticsAllowed) {
      //     logger.warn("Analytics events are currently blocked");
      //     return;
      //   }
      // } else if (isProduction) {
      //   if (!isAnalyticsAllowed) {
      //     logger.warn("Analytics events are currently blocked");
      //     return;
      //   }
      // }

      if (appData.REACT_APP_MARKETING_TELEMETRY === 0) {
        logger.warn(`Marketing events tracking disabled`);
        return;
      }

      const path = getCurrentPageUrl();
      const checkoutProduct = getCheckoutEventProduct(eventName, eventParams, newT2path);
      const leadCurrency = newT2path ? currencyType : currencyType || "USD";
      const reportOneSignalEventWithLogging = (params) => {
        reportOneSignalEvent(appData.REACT_APP_API_ENDPOINT, params)
          .then(() => logger.debug(`OneSignal ${params.event_name}`, params))
          .catch((error) =>
            logger.error(`OneSignal ${params.event_name}`, params, error)
          );
      };

      if (eventParams) {
        logger.event(` ${eventName} `, `Params:`, eventParams);
      } else {
        logger.event(` ${eventName}`);
      }

      switch (eventName) {
        case "landingPageView":
          if (canTrackMetaEvents()) {
            window.fbq("trackCustom", "landingPageView");
          }

          if (canTrackTikTokEvents()) {
            window.ttq.track("Landing Page View");
            logger.event(" TT event", "Landing Page View");
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("landing_page_view", {
              event_name: "landing_page_view",
              event_category: "landing_page",
              page_url: path,
            });
          }

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "PAGE_VIEW");
          }

          break;

        case "landingPageComplete":
          if (canTrackTikTokEvents()) {
            window.ttq.track("Landing Page Complete");
            logger.event("TT event", "Landing Page Complete");
          }
          break;

        case "getStartedClicked":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("click", {
          //     event_category: "click",
          //     event_label: "get_started_clicked",
          //   });
          // }

          if (canTrackGoogleEvents()) {
            window.gtag('event', 'click', {
              event_category: 'button',
              event_label: 'get_started_clicked',
            });
          }

          if (canTrackMetaEvents()) window.fbq("track", "AddToWishlist");

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "ADD_TO_WISHLIST");
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("get_started_clicked", {
              event_category: "click",
              event_name: "get_started_clicked",
            });
          }

          break;

        case "axonPageView": {
          if (canTrackAxonEvents()) {
            window.axon("track", "page_view");
          }
          break;
        }
        case "pageView":
          if (canTrackMetaEvents()) {
            window.fbq("trackCustom", "page_view");
            window.fbq("track", "ViewContent");
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("page_view", {
              event_name: "page_view",
            });
          }

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "PAGE_VIEW");
          }

          break;

        case "waitlistPageView":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("waitlist_page_view", {
          //     event_category: "waitlist_page_view",
          //     event_label: "waitlist_page_view",
          //   });
          // }

          if (canTrackGoogleEvents()) {
            window.gtag('event', 'waitlist_page_view', {
              event_category: "waitlist_page_view",
              event_label: "waitlist_page_view",
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("waitlist_page_view", {
              event_category: "waitlist_page_view",
              event_label: "waitlist_page_view",
              page_url: path,
            });
          }

          if (canTrackTikTokEvents()) {
            window.ttq.track("Waitlist Page View");
            logger.event("TT event", `Waitlist Page View`);
          }

          if (canTrackMetaEvents())
            window.fbq("trackCustom", "ViewContentWaitlist");

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "ADD_CART");
          }

          break;

        case "hpExpertPageView":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("hp_expert_page_view", {
          //     event_category: "hp_expert_page_view",
          //     event_label: "hp_expert_page_view",
          //   });
          // }

          if (canTrackGoogleEvents()) {
            window.gtag('event', 'hp_expert_page_view', {
              event_category: "hp_expert_page_view",
              event_label: "hp_expert_page_view",
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("hp_expert_page_view", {
              event_category: "hp_expert_page_view",
              event_label: "hp_expert_page_view",
              page_url: path,
            });
          }

          if (canTrackTikTokEvents()) {
            window.ttq.track("HP Expert Page View");
            logger.event("TT event", `HP Expert Page View`);
          }

          if (canTrackMetaEvents())
            window.fbq("trackCustom", "ViewContentHPexpert");

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "ADD_CART");
          }

          break;

        case "hpEssentialPageView":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("hp_essential_page_view", {
          //     event_category: "hp_essential_page_view",
          //     event_label: "hp_essential_page_view",
          //   });
          // }

          if (canTrackGoogleEvents()) {
            window.gtag('event', 'hp_essential_page_view', {
              event_category: "hp_essential_page_view",
              event_label: "hp_essential_page_view",
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("hp_essential_page_view", {
              event_category: "hp_essential_page_view",
              event_label: "hp_essential_page_view",
              page_url: path,
            });
          }

          if (canTrackTikTokEvents()) {
            window.ttq.track("HP Essential Page View");
            logger.event("TT event", `HP Essential Page View`);
          }

          if (canTrackMetaEvents())
            window.fbq("trackCustom", "ViewContentHPessential");

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "ADD_CART");
          }

          break;

        case "hp23dExpertPageView":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("hp23d_expert_page_view", {
          //     event_category: "hp23d_expert_page_view",
          //     event_label: "hp23d_expert_page_view",
          //   });
          // }

          if (canTrackGoogleEvents()) {
            window.gtag('event', 'hp23d_expert_page_view', {
              event_category: "hp23d_expert_page_view",
              event_label: "hp23d_expert_page_view",
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("hp23d_expert_page_view", {
              event_category: "hp23d_expert_page_view",
              event_label: "hp23d_expert_page_view",
              page_url: path,
            });
          }

          if (canTrackTikTokEvents()) {
            window.ttq.track("HP 23d Expert Page View");
            logger.event(`TT event`, `HP 23d Expert Page View`);
          }

          if (canTrackMetaEvents())
            window.fbq("trackCustom", "ViewContentHP23expert");

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "ADD_CART");
          }

          break;

        case "hp23dEssentialPageView":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("hp23d_essential_page_view", {
          //     event_category: "hp23d_essential_page_view",
          //     event_label: "hp23d_essential_page_view",
          //   });
          // }

          if (canTrackGoogleEvents()) {
            window.gtag('event', 'hp23d_essential_page_view', {
              event_category: "hp23d_essential_page_view",
              event_label: "hp23d_essential_page_view",
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("hp23d_essential_page_view", {
              event_category: "hp23d_essential_page_view",
              event_label: "hp23d_essential_page_view",
              page_url: path,
            });
          }

          if (canTrackTikTokEvents()) {
            window.ttq.track("HP 23d Essential Page View");
            logger.event(`TT event`, `HP 23d Essential Page View`);
          }

          if (canTrackMetaEvents())
            window.fbq("trackCustom", "ViewContentHP23essential");

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "ADD_CART");
          }

          break;

        case "hp3ExpertPageView":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("hp3_expert_page_view", {
          //     event_category: "hp3_expert_page_view",
          //     event_label: "hp3_expert_page_view",
          //   });
          // }

          if (canTrackGoogleEvents()) {
            window.gtag('event', 'hp3_expert_page_view', {
              event_category: "hp3_expert_page_view",
              event_label: "hp3_expert_page_view",
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("hp3_expert_page_view", {
              event_category: "hp3_expert_page_view",
              event_label: "hp3_expert_page_view",
              page_url: path,
            });
          }

          if (canTrackTikTokEvents()) {
            window.ttq.track("HP 3 Expert Page View");
            logger.event(`TT event`, `HP 3 Expert Page View`);
          }

          if (canTrackMetaEvents())
            window.fbq("trackCustom", "ViewContentHP3Expert");

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "ADD_CART");
          }

          break;

        case "hp3EssentialPageView":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("hp3_essential_page_view", {
          //     event_category: "hp3_essential_page_view",
          //     event_label: "hp3_essential_page_view",
          //   });
          // }

          if (canTrackGoogleEvents()) {
            window.gtag('event', 'hp3_essential_page_view', {
              event_category: "hp3_essential_page_view",
              event_label: "hp3_essential_page_view",
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("hp3_essential_page_view", {
              event_category: "hp3_essential_page_view",
              event_label: "hp3_essential_page_view",
              page_url: path,
            });
          }

          if (canTrackTikTokEvents()) {
            window.ttq.track("HP 3 Essential Page View");
            logger.event(`TT event`, `HP 3 Essential Page View`);
          }

          if (canTrackMetaEvents())
            window.fbq("trackCustom", "ViewContentHP3Essential");

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "ADD_CART");
          }

          break;

        case "upsellPageView":

          reportOneSignalEventWithLogging({
            cid: state.cid,
            event_name: OneSignalEventName.UPSALE_PAGE_VIEW,
            page_url: path,
            upsell_number:
              eventParams != null ? String(eventParams) : undefined,
          });


          if (canTrackMetaEvents()) {
            window.fbq("trackCustom", "upsale_page_view", { upsell_number: eventParams });
            window.fbq("track", "ViewContent");
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("upsale_page_view", {
              event_name: "upsale_page_view",
              upsell_number: eventParams
            });
          }

          break;

        case "upsellV2PageView":

          reportOneSignalEventWithLogging({
            cid: state.cid,
            event_name: OneSignalEventName.UPSALE_PAGE_VIEW,
            page_url: path,
            upsell_number:
              eventParams?.index != null ? String(eventParams.index) : undefined,
          });

          if (canTrackMetaEvents()) {
            window.fbq("trackCustom", "upsale_page_view", { upsell_number: eventParams.index });
            window.fbq("track", "ViewContent");
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("upsale_page_view", {
              event_name: "upsale_page_view",
              upsell_page_name: eventParams.upsell_name,
              upsell_number: eventParams.index
            });
          }

          break;

        case "quizStart":
          if (canTrackTikTokEvents()) {
            window.ttq.track("Quiz Start");
            logger.event("TT event", "Quiz Start");
          }
          if (canTrackAmplitudeEvents()) {
            amplitude.track("quiz_start", {
              event_category: "quiz_start",
              event_name: "quiz_start",
              page_url: path,
            });
          }
          break;

        case "quizPageView":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("quiz_page_view", {
          //     event_category: "quiz_page_view",
          //     event_label: "quiz_page_view",
          //     quiz_number: eventParams,
          //   });
          // }

          if (canTrackGoogleEvents()) {
            window.gtag('event', "quiz_page_view", {
              event_category: "quiz_page_view",
              event_label: "quiz_page_view",
              quiz_number: eventParams,
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("quiz_page_view", {
              event_category: "quiz_page_view",
              event_name: "quiz_page_view",
              quiz_number: eventParams,
            });
          }

          if (canTrackMetaEvents()) {
            window.fbq("trackCustom", "quizPageView");
            window.fbq("track", "ViewContent");
          }

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "PAGE_VIEW");
          }

          break;

        case "quizLoaderPopupClose":
          if (canTrackAmplitudeEvents()) {
            amplitude.track("loader_popup_closed", {
              event_category: "loader_popup_closed",
              event_name: "loader_popup_closed",
              popup_number: eventParams.popup_number,
              answer: eventParams.answer,
            });
          }
          break;


        case "leadFormSubmit":
          const { user_cid, leadParams } = getAnalyticsParams();

          if (leadParams) {
            // if (analyticsFirebase !== null)
            //   analyticsFirebase.logEvent("lead_form_submit", leadParams);

            if (canTrackGoogleEvents()) {
              const gaParams = {
                ...leadParams,
                currency: leadCurrency,
                form_name: "contact_form",
                page_location: path
              };
              window.gtag('event', "generate_lead", gaParams);
            }

            if (canTrackAmplitudeEvents()) {
              amplitude.track("lead_form_submit", {
                ...leadParams,
                event_category: "lead",
              });
            }
            const hashedLeadParams = { ...leadParams };
            hashedLeadParams.email = hashData(hashedLeadParams.email);

            if (canTrackMetaEvents()) {
              window.fbq("track", "Lead", { leadEventData: hashedLeadParams });
              window.fbq("trackCustom", "ViewContentContact");
            }

            if (canTrackSnaptrEvents()) {
              window.snaptr("track", "CUSTOM_EVENT_2");
            }
            logger.event("leadFormSubmit params", leadParams);
            logger.event(`leadFormSubmit hash params`, hashedLeadParams);

            if (canTrackAxonEvents()) {
              window.axon("track", "generate_lead", {
                currency: leadCurrency,
                user_id: user_cid,
              });
            }
            logger.event(`axon generate_lead params`, {
              currency: leadCurrency,
              user_id: user_cid,
            });

          } else {
            logger.error(`Lead form submit params not found`);
          }

          break;

        case "nameFormSubmit":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("name_form_submit", {
          //     event_category: "first_name",
          //     event_label: eventParams,
          //   });
          // }

          if (canTrackGoogleEvents()) {
            window.gtag('event', "form_submit", {
              event_category: "name_form",
              event_label: eventParams,
              form_type: "first_name"
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("name_form_submit", {
              event_category: "first_name",
              ...eventParams,
            });
          }

          if (canTrackMetaEvents())
            window.fbq("trackCustom", "ViewContentContact");

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "CUSTOM_EVENT_1");
          }

          break;

        case "setUserID":
          if (canUseAmplitudeEvents()) {
            amplitude.setUserId(eventParams.cid);
            logger.event(`Amplitude userID set for:`, eventParams.cid, `deviceId:`, amplitude.getDeviceId());
          }

          break;

        case "resetUserID":
          if (canUseAmplitudeEvents()) {
            // No amplitude.reset() - it would generate a new Device ID and break Session Replay.
            amplitude.flush().promise.then(() => {
              amplitude.setUserId(eventParams.cid);
              logger.event(`Amplitude userID reset and set for:`, eventParams.cid, `deviceId:`, amplitude.getDeviceId());
            });
          }
          break;

        case "completeRegistration":
          const { completeRegistrationParams } = getAnalyticsParams();

          if (completeRegistrationParams) {
            const { email, name, cid } = completeRegistrationParams;

            // if (
            //   analyticsFirebase &&
            //   typeof analyticsFirebase.setUserId === "function"
            // ) {
            //   analyticsFirebase.setUserId(cid);
            //   logger.event(`analyticsFirebase userId set for:`, cid);
            // } else {
            //   logger.error(
            //     `analyticsFirebase or setUserId method not available`
            //   );
            // }

            if (canTrackGoogleEvents()) {
              window.gtag('set', { user_id: cid });

              window.gtag('event', 'sign_up', {
                method: 'email',
                user_id: cid
              });
            }

            if (canUseAmplitudeEvents()) {
              // amplitude.setUserId(cid);
              // logger.event(`amplitude user id set for:`, cid);

              userIdentifyEvent("user_email", email);
              userIdentifyEvent("user_name", name);
            }

            const hashedCompleteRegistrationParams = {
              email: hashData(email),
              name: hashData(name),
              cid: hashData(cid),
            };

            if (canTrackTikTokEvents()) {
              if (typeof window.ttq.identify === "function") {
                window.ttq.identify({
                  email: hashedCompleteRegistrationParams.email,
                  name: hashedCompleteRegistrationParams.name,
                  external_id: hashedCompleteRegistrationParams.cid,
                });
              }
              window.ttq.track("Complete Registration");
              logger.event(
                `TT Complete Registration`,
                `params: `,
                hashedCompleteRegistrationParams
              );
            }
          } else {
            logger.error(`Complete registration params not found`);
          }

          break;

        case "conversion":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("conversion", {
          //     send_to: "AW-666333408/I4bYCIa4_MMBEODh3b0C",
          //   });
          // }

          if (canTrackGoogleEvents()) {
            window.gtag('event', 'conversion', {
              send_to: 'AW-666333408/I4bYCIa4_MMBEODh3b0C'
            });
          }

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "CUSTOM_EVENT_3");
          }

          break;

        case "viewItem":
          if (canTrackAxonEvents()) {
            const axon_view_item_data = eventParams;
            window.axon("track", "view_item", axon_view_item_data);
            logger.event(`axon view_item`, axon_view_item_data);
          }
          break;

        case "planPageView":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("plan_page_view", {
          //     event_category: "plan_page_view",
          //     event_label: "plan_page_view",
          //   });
          // }

          reportOneSignalEventWithLogging({
            cid: state.cid,
            event_name: OneSignalEventName.PLAN_PAGE_VIEW,
            page_url: path,
          });

          if (canTrackGoogleEvents()) {
            window.gtag('event', "plan_page_view", {
              event_category: "plan_page_view",
              event_label: "plan_page_view",
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("plan_page_view", {
              event_category: "plan_page_view",
              event_label: "plan_page_view",
              page_url: path,
            });
          }

          if (canTrackTikTokEvents()) {
            window.ttq.track("Plan Page View");
            logger.event("TT event", `Plan Page View`);
          }

          if (canTrackMetaEvents())
            window.fbq("trackCustom", "ViewContentPlanPage");

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "ADD_CART");
          }

          break;

        case "initiateCheckout":

          if (eventParams) {
            if (canTrackTikTokEvents()) {
              const initialProductData = {
                value: checkoutProduct.value,
                currency: eventParams?.currency || "USD",
                contents: [
                  {
                    content_id: checkoutProduct.productId,
                    content_name: checkoutProduct.name,
                    price: checkoutProduct.value,
                  },
                ],
                content_type: "product",
              };

              window.ttq.track("InitiateCheckout", initialProductData);
              logger.event(`TT event`, "Initiate Checkout", initialProductData);
            }
            if (canTrackAxonEvents()) {

              const axon_add_to_cart_data = {
                currency: eventParams?.currency || "USD",
                items: [
                  {
                    item_variant_id: checkoutProduct.productId,
                    item_id: checkoutProduct.productId,
                    item_name: checkoutProduct.itemName,
                    image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                    price: checkoutProduct.value,
                    quantity: 1,
                    item_category_id: 317,
                    item_brand: "memoryOS",
                  }
                ],
                value: checkoutProduct.value
              }


              window.axon("track", "add_to_cart", axon_add_to_cart_data);
              logger.event(`axon add_to_cart`, axon_add_to_cart_data);
            }

          } else {
            const currentOrderData = JSON.parse(
              localStorage.getItem("MemoryOSCurrentOrder")
            );

            if (canTrackTikTokEvents()) {
              const initialProductData = {
                value: currentOrderData.full_price,
                currency: eventParams?.currency || "USD",
                contents: [
                  {
                    content_id: currentOrderData.product_id,
                    content_name: currentOrderData.plan_name,
                    price: currentOrderData.full_price,
                  },
                ],
                content_type: "product",
              };

              window.ttq.track("InitiateCheckout", initialProductData);
              logger.event(`TT event`, "Initiate Checkout", initialProductData);
            }

            if (canTrackAxonEvents()) {

              const currentOrder = JSON.parse(localStorage.getItem("MemoryOSCurrentOrder"));

              const axon_add_to_cart_data = {
                currency: eventParams?.currency || "USD",
                items: [
                  {
                    item_variant_id: currentOrder.product_id,
                    item_id: currentOrder.product_id,
                    item_name: `${currentOrder.plan_name} ${currentOrder?.thankyou_page_title}`,
                    image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                    price: currentOrder.full_price,
                    quantity: 1,
                    item_category_id: 317,
                    item_brand: "memoryOS",
                  }
                ],
                value: currentOrder.full_price
              }


              window.axon("track", "add_to_cart", axon_add_to_cart_data);
              logger.event(`axon add_to_cart`, axon_add_to_cart_data);
            }
          }

          break;

        case "paymentFormRenderSuccess":
          if (canTrackAmplitudeEvents()) {
            amplitude.track("payment_form_render_success", {
              event_category: "payment_form_render_success",
              event_label: "payment_form_render_success",
              page_url: path
            });
          }

          // if (analyticsFirebase !== null)
          //   analyticsFirebase.logEvent("payment_form_render_success", {
          //     event_category: "payment_form_render_success",
          //     event_label: "payment_form_render_success",
          //     page_url: path
          //   });

          if (canTrackGoogleEvents()) {
            window.gtag('event', "payment_form_render_success", {
              event_category: "payment_form_render_success",
              event_label: "payment_form_render_success",
              page_url: path
            });
          }

          break;

        case "paymentFormRenderFail":
          if (canTrackAmplitudeEvents()) {
            amplitude.track("payment_form_render_fail", {
              event_category: "payment_form_render_fail",
              event_label: "payment_form_render_fail",
              page_url: path
            });
          }

          // if (analyticsFirebase !== null)
          //   analyticsFirebase.logEvent("payment_form_render_fail", {
          //     event_category: "payment_form_render_fail",
          //     event_label: "payment_form_render_fail",
          //     page_url: path
          //   });

          if (canTrackGoogleEvents()) {
            window.gtag('event', "payment_form_render_fail", {
              event_category: "payment_form_render_fail",
              event_label: "payment_form_render_fail",
              page_url: path
            });
          }

          break;
        case "paymentFormRenderTimeOut":
          if (canTrackAmplitudeEvents()) {
            amplitude.track("payment_form_render_timeout", {
              event_category: "payment_form_render_timeout",
              event_label: "payment_form_render_timeout",
              page_url: path
            });
          }

          // if (analyticsFirebase !== null)
          //   analyticsFirebase.logEvent("payment_form_render_timeout", {
          //     event_category: "payment_form_render_timeout",
          //     event_label: "payment_form_render_timeout",
          //     page_url: path
          //   });

          if (canTrackGoogleEvents()) {
            window.gtag('event', "payment_form_render_timeout", {
              event_category: "payment_form_render_timeout",
              event_label: "payment_form_render_timeout",
              page_url: path
            });
          }
          break;

        case "removeFromCart":

          if (eventParams) {
            if (canTrackAxonEvents()) {

              const axon_remove_from_cart = {
                currency: eventParams?.currency || "USD",
                items: [
                  {
                    item_variant_id: checkoutProduct.productId,
                    item_id: checkoutProduct.productId,
                    item_name: checkoutProduct.itemName,
                    image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                    price: checkoutProduct.value,
                    quantity: 1,
                    item_category_id: 317,
                    item_brand: "memoryOS",
                  }
                ],
                value: checkoutProduct.value
              }


              window.axon("track", "remove_from_cart", axon_remove_from_cart);
              logger.event(`axon remove_from_cart`, axon_remove_from_cart);
            }
          } else {
            if (canTrackAxonEvents()) {

              const currentOrder = JSON.parse(localStorage.getItem("MemoryOSCurrentOrder"));

              const axon_remove_from_cart = {
                currency: eventParams?.currency || "USD",
                items: [
                  {
                    item_variant_id: currentOrder.product_id,
                    item_id: currentOrder.product_id,
                    item_name: `${currentOrder.plan_name} ${currentOrder?.thankyou_page_title}`,
                    image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                    price: currentOrder.full_price,
                    quantity: 1,
                    item_category_id: 317,
                    item_brand: "memoryOS",
                  }
                ],
                value: currentOrder.full_price
              }


              window.axon("track", "remove_from_cart", axon_remove_from_cart);
              logger.event(`axon remove_from_cart`, axon_remove_from_cart);
            }
          }

          break;

        case "beginCheckout":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("begin_checkout", {
          //     event_category: "begin_checkout",
          //     event_label: "begin_checkout",
          //   });
          // }

          reportOneSignalEventWithLogging({
            cid: state.cid,
            event_name: OneSignalEventName.BEGIN_CHECKOUT,
            page_url: path,
          });

          if (canTrackGoogleEvents()) {
            window.gtag('event', "begin_checkout", {
              event_category: "begin_checkout",
              event_label: "begin_checkout",
              currency: eventParams?.currency || "USD",
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("begin_checkout", {
              event_category: "begin_checkout",
              event_label: "begin_checkout",
            });
          }

          if (canTrackMetaEvents()) {
            window.fbq("track", "InitiateCheckout");
          }

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "START_CHECKOUT");
          }

          logger.log(`beginCheckout eventParams`, eventParams)

          if (canTrackTikTokEvents()) {
            const initialPlaceAnOrderData = {
              value: checkoutProduct.value,
              currency: eventParams?.currency || "USD",
              contents: [
                {
                  content_id: checkoutProduct.productId,
                  content_type: "product",
                  description: checkoutProduct.name,
                  price: checkoutProduct.value,
                  quantity: 1,
                },
              ],
              content_type: "product",
            };

            window.ttq.track("PlaceAnOrder", initialPlaceAnOrderData);

            logger.event(`TT event`, "Place An Order", initialPlaceAnOrderData);
          }

          if (canTrackAxonEvents()) {

            const axon_begin_checkout = {
              currency: eventParams?.currency || "USD",
              items: [
                {
                  item_variant_id: checkoutProduct.productId,
                  item_id: checkoutProduct.productId,
                  item_name: checkoutProduct.itemName,
                  image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                  price: checkoutProduct.value,
                  quantity: 1,
                  item_category_id: 317,
                  item_brand: "memoryOS",
                }
              ],
              value: checkoutProduct.value
            }


            window.axon("track", "begin_checkout", axon_begin_checkout);
            logger.event(`axon begin_checkout`, axon_begin_checkout);
          }

          if (canTrackDressxEvents()) {
            const dressxParams = {
              id: checkoutProduct.productId,
              price: Number(checkoutProduct.value),
              currency: eventParams?.currency || currencyType || "USD",
            };
            window.dxq("track", "checkout_started", dressxParams);
            logger.event("DRESSX checkout_started", dressxParams);
          }

          break;

        case "addPaymentInfo":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("add_payment_info", {
          //     event_category: "add_payment_info",
          //     event_label: "add_payment_info",
          //   });
          // }

          if (eventParams) {
            if (canTrackGoogleEvents()) {
              window.gtag('event', "add_payment_info", {
                event_category: "add_payment_info",
                event_label: "add_payment_info",
                currency: eventParams?.currency || "USD",
              });
            }

            if (canTrackAmplitudeEvents()) {
              amplitude.track("add_payment_info", {
                event_category: "add_payment_info",
                event_label: "add_payment_info",
              });
            }

            if (canTrackMetaEvents()) window.fbq("trackCustom", "addPaymentInfo");

            if (canTrackSnaptrEvents()) {
              window.snaptr("track", "ADD_BILLING");
            }

            if (canTrackAxonEvents()) {

              const axon_add_payment_info = {
                currency: eventParams?.currency || "USD",
                items: [
                  {
                    item_variant_id: checkoutProduct.productId,
                    item_id: checkoutProduct.productId,
                    item_name: checkoutProduct.itemName,
                    image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                    price: checkoutProduct.value,
                    quantity: 1,
                    item_category_id: 317,
                    item_brand: "memoryOS",
                  }
                ],
                payment_type: "credit_card",
                value: checkoutProduct.value
              }


              window.axon("track", "add_payment_info", axon_add_payment_info);
              logger.event(`axon add_payment_info`, axon_add_payment_info);
            }
          } else {
            if (canTrackGoogleEvents()) {
              window.gtag('event', "add_payment_info", {
                event_category: "add_payment_info",
                event_label: "add_payment_info",
                currency: eventParams?.currency || "USD",
              });
            }

            if (canTrackAmplitudeEvents()) {
              amplitude.track("add_payment_info", {
                event_category: "add_payment_info",
                event_label: "add_payment_info",
              });
            }

            if (canTrackMetaEvents()) window.fbq("trackCustom", "addPaymentInfo");

            if (canTrackSnaptrEvents()) {
              window.snaptr("track", "ADD_BILLING");
            }

            if (canTrackAxonEvents()) {

              const currentOrder = JSON.parse(localStorage.getItem("MemoryOSCurrentOrder"));

              const axon_add_payment_info = {
                currency: eventParams?.currency || "USD",
                items: [
                  {
                    item_variant_id: currentOrder.product_id,
                    item_id: currentOrder.product_id,
                    item_name: `${currentOrder.plan_name} ${currentOrder?.thankyou_page_title}`,
                    image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                    price: currentOrder.full_price,
                    quantity: 1,
                    item_category_id: 317,
                    item_brand: "memoryOS",
                  }
                ],
                payment_type: "credit_card",
                value: currentOrder.full_price
              }


              window.axon("track", "add_payment_info", axon_add_payment_info);
              logger.event(`axon add_payment_info`, axon_add_payment_info);
            }
          }

          break;


        case "upsellAddPaymentInfo":
          if (canTrackGoogleEvents()) {
            window.gtag('event', "add_payment_info", {
              event_category: "add_payment_info",
              event_label: "add_payment_info",
              currency: eventParams?.currency || "USD",
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("add_payment_info", {
              event_category: "add_payment_info",
              event_label: "add_payment_info",
            });
          }

          if (canTrackMetaEvents()) window.fbq("trackCustom", "addPaymentInfo");

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "ADD_BILLING");
          }

          if (canTrackAxonEvents()) {

            const axon_add_payment_info = {
              currency: eventParams?.currency || "USD",
              items: [
                {
                  item_variant_id: checkoutProduct.productId,
                  item_id: checkoutProduct.productId,
                  item_name: checkoutProduct.itemName,
                  image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                  price: checkoutProduct.value,
                  quantity: 1,
                  item_category_id: 317,
                  item_brand: "memoryOS",
                }
              ],
              payment_type: "credit_card",
              value: checkoutProduct.value
            }


            window.axon("track", "add_payment_info", axon_add_payment_info);
            logger.event(`axon add_payment_info`, axon_add_payment_info);
          }
          break;

        case "purchase": {
          // if (analyticsFirebase !== null)
          //   analyticsFirebase.logEvent("purchase", eventParams);

          const { google_ads_value, meta_ads_value, ...purchaseEventParams } = eventParams;
          eventParams = purchaseEventParams;

          reportOneSignalEventWithLogging({
            cid: state.cid,
            event_name: OneSignalEventName.PURCHASE,
            page_url: path,
            product_id:
              eventParams?.product_id != null
                ? String(eventParams.product_id)
                : undefined,
          });

          if (canTrackGoogleEvents()) {
            const googleAdsPurchaseParams = { ...eventParams };
            if (isGoogleAdsTraffic && google_ads_value != null) {
              googleAdsPurchaseParams.value = google_ads_value;
            }
            window.gtag('event', "purchase", googleAdsPurchaseParams);
            logger.debug(`Google ADS purchase params:`, googleAdsPurchaseParams);

          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("purchase", { ...eventParams, page_url: path });
            logger.debug(`Amplitude purchase params:`, eventParams);

          }

          if (canTrackMetaEvents()) {
            let price = 1;
            if (newT2path) {
              price = eventParams.order_price;
            } else if (eventParams.order_price === 0) {
              if (eventParams.conversion_price) {
                price = eventParams.conversion_price * 0.3;
              }
            } else {
              price = eventParams.order_price;
            }
            const metaParams = {
              value: price.toString(),
              currency: eventParams.currency,
              eventID: eventParams.event_id,
              predicted_ltv: 85
            };
            if (isMetaAdsTraffic && meta_ads_value != null) {
              metaParams.value = meta_ads_value;
            }
            window.fbq("track", "Purchase", metaParams);
            logger.debug(`Meta purchase params:`, metaParams);

          }

          if (canTrackSnaptrEvents()) {
            const snapParams = {
              description: eventParams.description,
              item_category: "Subscription",
              price: eventParams.value,
              currency: eventParams?.currency || "USD",
              number_items: 1,
              success: 1,
              client_dedup_id: eventParams.event_id,
              transaction_id: eventParams.transaction_id,
            };
            logger.debug(`Purchase snap-params: ${snapParams}`);

            window.snaptr("track", "PURCHASE", snapParams);
          }

          if (canTrackTikTokEvents()) {
            const initialCompletePaymentData = {
              value: eventParams.value,
              currency: eventParams?.currency || "USD",
              user: {
                email: hashData(state.email),
              },
              contents: [
                {
                  content_id: eventParams.product_id,
                  description: eventParams.description,
                  quantity: 1,
                  price: eventParams.value,
                },
              ],
              content_type: "product",
              status: "fulfilled",
            };
            window.ttq.track("CompletePayment", initialCompletePaymentData, {
              event_id: eventParams.event_id,
            });
            logger.event(
              `TT event`,
              "Complete Payment",
              initialCompletePaymentData
            );
          }
          if (canTrackAxonEvents()) {

            const { user_cid } = getAnalyticsParams();

            window.axon("track", "purchase", {
              currency: eventParams?.currency || "USD",
              items: [
                {
                  item_variant_id: eventParams.product_id,
                  item_id: eventParams.product_id,
                  item_name: eventParams.description,
                  image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                  price: eventParams.value,
                  quantity: 1,
                  item_category_id: 317,
                  item_brand: "memoryOS",
                }
              ],
              transaction_id: eventParams.transaction_id,
              value: eventParams.value,
              user_id: user_cid
            })
            logger.event(`axon purchase`, {
              currency: eventParams?.currency || "USD",
              items: [
                {
                  item_variant_id: eventParams.product_id,
                  item_id: eventParams.product_id,
                  item_name: eventParams.description,
                  image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                  price: eventParams.value,
                  quantity: 1,
                  item_category_id: 317,
                  item_brand: "memoryOS",
                }
              ],
              transaction_id: eventParams.transaction_id,
              value: eventParams.value,
              user_id: user_cid
            });
          }



          if (canTrackDressxEvents()) {
            const dressxParams = {
              id: eventParams.product_id,
              price: Number(eventParams.value),
              currency: eventParams?.currency || currencyType || "USD",
            };
            window.dxq("track", "order_created", dressxParams);
            logger.event("DRESSX order_created", dressxParams);
          }

          break;
        }

        case "purchaseUpsale":
          // if (analyticsFirebase !== null)
          //   analyticsFirebase.logEvent("purchase", eventParams);

          reportOneSignalEventWithLogging({
            cid: state.cid,
            event_name: OneSignalEventName.PURCHASE,
            page_url: path,
            product_id:
              eventParams?.product_id != null
                ? String(eventParams.product_id)
                : undefined,
          });

          // TODO: disabled google ads purchase event
          // if (canTrackGoogleEvents()) {
          //   window.gtag('event', "purchase", eventParams);
          //   logger.debug(`Google ADS purchase params:`, eventParams);

          // }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("purchase", { ...eventParams, page_url: path });
            logger.debug(`Amplitude purchase params:`, eventParams);

          }
          // TODO: disabled meta purchase event
          // if (canTrackMetaEvents()) {
          //   let price = 1;
          //   if (eventParams.order_price === 0) {
          //     if (eventParams.conversion_price) {
          //       price = eventParams.conversion_price * 0.3;
          //     }
          //   } else {
          //     price = eventParams.order_price;
          //   }
          //   const metaParams = {
          //     value: price.toString(),
          //     currency: eventParams.currency,
          //     eventID: eventParams.event_id,
          //   };
          //   window.fbq("track", "Purchase", metaParams);
          //   logger.debug(`Meta purchase params:`, metaParams);

          // }

          if (canTrackSnaptrEvents()) {
            const snapParams = {
              description: eventParams.description,
              item_category: "Subscription",
              price: eventParams.value,
              currency: eventParams?.currency || "USD",
              number_items: 1,
              success: 1,
              client_dedup_id: eventParams.event_id,
              transaction_id: eventParams.transaction_id,
            };
            logger.debug(`Purchase snap-params: ${snapParams}`);

            window.snaptr("track", "PURCHASE", snapParams);
          }

          if (canTrackTikTokEvents()) {
            const initialCompletePaymentData = {
              value: eventParams.value,
              currency: eventParams?.currency || "USD",
              user: {
                email: hashData(state.email),
              },
              contents: [
                {
                  content_id: eventParams.product_id,
                  description: eventParams.description,
                  quantity: 1,
                  price: eventParams.value,
                },
              ],
              content_type: "product",
              status: "fulfilled",
            };
            window.ttq.track("CompletePayment", initialCompletePaymentData, {
              event_id: eventParams.event_id,
            });
            logger.event(
              `TT event`,
              "Complete Payment",
              initialCompletePaymentData
            );
          }
          if (canTrackAxonEvents()) {

            const { user_cid } = getAnalyticsParams();

            window.axon("track", "purchase", {
              currency: eventParams?.currency || "USD",
              items: [
                {
                  item_variant_id: eventParams.product_id,
                  item_id: eventParams.product_id,
                  item_name: eventParams.description,
                  image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                  price: eventParams.value,
                  quantity: 1,
                  item_category_id: 317,
                  item_brand: "memoryOS",
                }
              ],
              transaction_id: eventParams.transaction_id,
              value: eventParams.value,
              user_id: user_cid
            })
            logger.event(`axon purchase`, {
              currency: eventParams?.currency || "USD",
              items: [
                {
                  item_variant_id: eventParams.product_id,
                  item_id: eventParams.product_id,
                  item_name: eventParams.description,
                  image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                  price: eventParams.value,
                  quantity: 1,
                  item_category_id: 317,
                  item_brand: "memoryOS",
                }
              ],
              transaction_id: eventParams.transaction_id,
              value: eventParams.value,
              user_id: user_cid
            });
          }

          if (canTrackDressxEvents()) {
            const dressxParams = {
              id: eventParams.product_id,
              price: Number(eventParams.value),
              currency: eventParams?.currency || currencyType || "USD",
            };
            window.dxq("track", "order_created", dressxParams);
            logger.event("DRESSX order_created", dressxParams);
          }

          break;


        case "beginCheckoutPage":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("begin_checkout", {
          //     event_category: "begin_checkout",
          //     event_label: "begin_checkout",
          //   });
          // }

          if (canTrackGoogleEvents()) {
            window.gtag('event', "begin_checkout", {
              event_category: "begin_checkout",
              event_label: "begin_checkout",
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("begin_checkout", {
              event_category: "begin_checkout",
              event_label: "begin_checkout",
            });
          }

          if (canTrackMetaEvents()) {
            window.fbq("track", "InitiateCheckout");
          }

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "START_CHECKOUT");
          }

          if (canTrackTikTokEvents()) {

            const initialPlaceAnOrderData = {
              value: eventParams.full_price,
              currency: eventParams?.currency || "USD",
              contents: [
                {
                  content_id: eventParams.product_id,
                  content_type: "product",
                  description: eventParams.plan_name,
                  price: eventParams.full_price,
                  quantity: 1,
                },
              ],
              content_type: "product",
            };

            window.ttq.track("PlaceAnOrder", initialPlaceAnOrderData);

            logger.event(`TT event`, "Place An Order", initialPlaceAnOrderData);
          }

          if (canTrackAxonEvents()) {

            const axon_begin_checkout = {
              currency: eventParams?.currency || "USD",
              items: [
                {
                  item_variant_id: eventParams.product_id,
                  item_id: eventParams.product_id,
                  item_name: eventParams.plan_name,
                  image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                  price: eventParams.full_price,
                  quantity: 1,
                  item_category_id: 317,
                  item_brand: "memoryOS",
                }
              ],
              value: eventParams.full_price
            }


            window.axon("track", "begin_checkout", axon_begin_checkout);
            logger.event(`axon begin_checkout`, axon_begin_checkout);
          }

          if (canTrackDressxEvents()) {
            const dressxParams = {
              id: eventParams.product_id,
              price: Number(eventParams.full_price),
              currency: eventParams?.currency || currencyType || "USD",
            };
            window.dxq("track", "checkout_started", dressxParams);
            logger.event("DRESSX checkout_started", dressxParams);
          }

          break;

        case "addPaymentInfoCheckout":
          // if (analyticsFirebase !== null) {
          //   analyticsFirebase.logEvent("add_payment_info", {
          //     event_category: "add_payment_info",
          //     event_label: "add_payment_info",
          //   });
          // }
          if (canTrackGoogleEvents()) {
            window.gtag('event', "add_payment_info", {
              event_category: "add_payment_info",
              event_label: "add_payment_info",
            });
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("add_payment_info", {
              event_category: "add_payment_info",
              event_label: "add_payment_info",
            });
          }

          if (canTrackMetaEvents()) window.fbq("trackCustom", "addPaymentInfo");

          if (canTrackSnaptrEvents()) {
            window.snaptr("track", "ADD_BILLING");
          }

          if (canTrackAxonEvents()) {

            // const currentOrder = JSON.parse(localStorage.getItem("MemoryOSCurrentOrder"));

            const axon_add_payment_info = {
              currency: eventParams?.currency || "USD",
              items: [
                {
                  item_variant_id: eventParams.product_id,
                  item_id: eventParams.product_id,
                  item_name: eventParams.plan_name,
                  image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                  price: eventParams.full_price,
                  quantity: 1,
                  item_category_id: 317,
                  item_brand: "memoryOS",
                }
              ],
              payment_type: "credit_card",
              value: eventParams.full_price
            }


            window.axon("track", "add_payment_info", axon_add_payment_info);
            logger.event(`axon add_payment_info`, axon_add_payment_info);
          }

          break;

        case "purchaseCheckout":
          // if (analyticsFirebase !== null)
          //   analyticsFirebase.logEvent("purchase", eventParams);
          if (canTrackGoogleEvents()) {
            window.gtag('event', "purchase", eventParams);
          }

          if (canTrackAmplitudeEvents()) {
            amplitude.track("purchase", { ...eventParams, page_url: path });
          }

          if (canTrackMetaEvents()) {
            let price = 1;
            if (eventParams.order_price === 0) {
              if (eventParams.conversion_price) {
                price = eventParams.conversion_price * 0.3;
              }
            } else {
              price = eventParams.order_price;
            }
            const metaParams = {
              value: price.toString(),
              currency: eventParams?.currency || "USD",
              eventID: eventParams.event_id,
            };
            window.fbq("track", "Purchase", metaParams);
          }

          if (canTrackSnaptrEvents()) {
            const snapParams = {
              description: eventParams.description,
              item_category: "Subscription",
              price: eventParams.value,
              currency: eventParams?.currency || "USD",
              number_items: 1,
              success: 1,
              client_dedup_id: eventParams.event_id,
              transaction_id: eventParams.transaction_id,
            };
            console.warn(`Purchase snap-params: ${snapParams}`);

            window.snaptr("track", "PURCHASE", snapParams);
          }

          if (canTrackTikTokEvents()) {
            const initialCompletePaymentData = {
              value: eventParams.value,
              currency: eventParams?.currency || "USD",
              user: {
                email: hashData(eventParams.email),
              },
              contents: [
                {
                  content_id: eventParams.product_id,
                  description: eventParams.description,
                  quantity: 1,
                  price: eventParams.value,
                },
              ],
              content_type: "product",
              status: "fulfilled",
            };
            window.ttq.track("CompletePayment", initialCompletePaymentData, {
              event_id: eventParams.event_id,
            });
            logger.event(
              `TT event`,
              "Complete Payment",
              initialCompletePaymentData
            );
          }
          if (canTrackAxonEvents()) {

            const { user_cid } = getAnalyticsParams();

            window.axon("track", "purchase", {
              currency: eventParams?.currency || "USD",
              items: [
                {
                  item_variant_id: eventParams.product_id,
                  item_id: eventParams.product_id,
                  item_name: eventParams.description,
                  image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                  price: eventParams.value,
                  quantity: 1,
                  item_category_id: 317,
                  item_brand: "memoryOS",
                }
              ],
              transaction_id: eventParams.transaction_id,
              value: eventParams.value,
              user_id: user_cid
            })
            logger.event(`axon purchase`, {
              currency: eventParams?.currency || "USD",
              items: [
                {
                  item_variant_id: eventParams.product_id,
                  item_id: eventParams.product_id,
                  item_name: eventParams.description,
                  image_url: "https://firebasestorage.googleapis.com/v0/b/memoryos-web-funnel.appspot.com/o/newLogoHP.png?alt=media&token=856ce76d-0b46-4e14-9025-cd315aae4230",
                  price: eventParams.value,
                  quantity: 1,
                  item_category_id: 317,
                  item_brand: "memoryOS",
                }
              ],
              transaction_id: eventParams.transaction_id,
              value: eventParams.value,
              user_id: user_cid
            });
          }



          if (canTrackDressxEvents()) {
            const dressxParams = {
              id: eventParams.product_id,
              price: Number(eventParams.value),
              currency: eventParams?.currency || currencyType || "USD",
            };
            window.dxq("track", "order_created", dressxParams);
            logger.event("DRESSX order_created", dressxParams);
          }

          break;

        case "failPurchase":

          reportOneSignalEventWithLogging({
            cid: state.cid,
            event_name: OneSignalEventName.PURCHASE_FAIL,
            page_url: path,
          });

          if (canTrackAmplitudeEvents()) {
            amplitude.track("purchase_fail", {
              event_category: "purchase_fail",
              event_label: "purchase_fail",
            });
          }

          // if (analyticsFirebase !== null)
          //   analyticsFirebase.logEvent("purchase_fail");
          if (canTrackGoogleEvents()) {
            window.gtag('event', "purchase_fail");
          }

          break;

        case "purchaseUpsaleClose":
          if (canTrackAmplitudeEvents()) {
            amplitude.track("purchase_upsale_close", {
              event_category: "purchase_upsale_close",
              event_label: "purchase_upsale_close",
              upsell_number: eventParams
            });
          }

          // if (analyticsFirebase !== null)
          //   analyticsFirebase.logEvent("purchase_upsale_close", {
          //     event_category: "purchase_upsale_close",
          //     event_label: "purchase_upsale_close",
          //     upsell_number: eventParams
          //   });

          if (canTrackGoogleEvents()) {
            window.gtag('event', "purchase_upsale_close", {
              event_category: "purchase_upsale_close",
              event_label: "purchase_upsale_close",
              upsell_number: eventParams
            });
          }

          break;

        case "signUp":
          if (canTrackAxonEvents()) {
            const { user_cid } = getAnalyticsParams();

            window.axon("track", "sign_up",
              {
                method: eventParams,
                user_id: user_cid,
              });

            logger.event(`axon sign_up`, {
              method: eventParams,
              user_id: user_cid,
            });
          }
          break;

        case "signUpPageView":
          if (canTrackAmplitudeEvents()) {
            amplitude.track("sign_up_page_view", eventParams);
          }

          break

        case "webviewToExternal":
          if (canTrackAmplitudeEvents()) {
            amplitude.track("webview_to_external", { event_category: "webview_to_external", page_url: path });
          }

          // if (analyticsFirebase !== null)
          //   analyticsFirebase.logEvent("webview_to_external", { event_category: "webview_to_external", page_url: path });

          if (canTrackGoogleEvents()) {
            window.gtag('event', "webview_to_external", {
              event_category: "webview_to_external",
              page_url: path
            });
          }

          break;

        case "redirectToApp":
          if (canTrackAmplitudeEvents()) {
            // Returned so the caller can wait until the event is sent before leaving the site.
            return amplitude.track("redirect_to_app", {
              event_name: "redirect_to_app",
              event_category: "click",
              page_url: path,
            }).promise;
          }

          break;

        default:
          console.error(`Analytics event not handled: ${eventName}`);
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAnalyticsAllowed,
      stagingEventsStatus,
      appData.REACT_APP_MARKETING_TELEMETRY,
      currencyType,
      state,
      canTrackMetaEvents,
      canTrackSnaptrEvents,
      canTrackGoogleEvents,
      canUseAmplitudeEvents,
      canTrackAmplitudeEvents,
      canTrackTikTokEvents,
      canTrackAxonEvents,
      canTrackDressxEvents,
      isGoogleAdsTraffic,
      newT2path,
      isMetaAdsTraffic,
    ]
  );

  useEffect(() => {
    if (!isProduction && !stagingEventsStatus) {
      return;
    }

    if (trigerUserAnalytics) {
      analyticsEvent("completeRegistration");
      analyticsEvent("leadFormSubmit");
      setTrigerUserAnalytics(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isProduction,
    stagingEventsStatus,
    isAnalyticsAllowed,
    trigerUserAnalytics,
  ]);

  const initSnaptr = async () => {
    if (!isProduction && !stagingEventsStatus) {
      if (!isAnalyticsAllowed) {
        console.warn(`Init Snaptr are currently blocked`);
        return;
      }
    } else if (isProduction) {
      if (!isAnalyticsAllowed) {
        logger.warn("Init Snaptr are currently blocked");
        return;
      }
    }

    if (userGeoData === null) {
      console.log("fetch geo data");
      try {
        const response = await fetch(apiGeoUrl);
        if (response.ok) {
          const geoData = await response.json();
          localStorage.setItem("MemoryOSGeoData", JSON.stringify(geoData));
          setUserGeoData(geoData);
        } else {
          console.error("Error response geo:", response.status);
        }
      } catch (error) {
        console.error("Error fetch geo:", error);
      }
    } else if (userGeoData !== null) {
      const hashedEmail =
        userData?.userEmail !== undefined
          ? await shaEmailHash(userData.userEmail)
          : null;

      const snaptrInitData = {
        user_email: userData?.userEmail ?? null,
        user_hashed_email: hashedEmail,
        firstname: userData?.userName ?? null,
        ip_address: userGeoData.ip,
        geo_city: userGeoData.city,
        geo_region: userGeoData.state,
        geo_postal_code: userGeoData.zip,
        geo_country: userGeoData.country,
      };

      const cleanedSnaptrData = removeUndefOrNullFields(snaptrInitData);
      if (userData === null) {
        console.log("Processing init snaptr event: send only geo data");
      } else {
        console.log("Processing init snaptr event: send geo + user data");
      }
      window.snaptr(
        "init",
        "828579a5-bbbe-4d06-bc6a-53cc466efb08",
        cleanedSnaptrData
      );
    }
  };

  const snaptrInitializedRef = useRef(false);

  useEffect(() => {
    if (
      snaptrInitializedRef.current ||
      isAnalyticsAllowed === null ||
      snaptrEventStatus !== true
    ) {
      return;
    }

    if (typeof window.snaptr !== "function") {
      return;
    }

    snaptrInitializedRef.current = true;
    initSnaptr();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, userGeoData, isAnalyticsAllowed, snaptrEventStatus]);

  const removeUndefOrNullFields = (obj) => {
    const newObj = {};
    for (const key in obj) {
      if (obj[key] !== undefined && obj[key] !== null) {
        newObj[key] = obj[key];
      }
    }
    return newObj;
  };

  const shaEmailHash = async (email) => {
    const msgBuffer = new TextEncoder().encode(email.toLowerCase());
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    return hashHex;
  };

  const contextValue = {
    analyticsEvent,
    setSnaptrUserData,
    userIdentifyEvent,
  };

  return (
    <EventContext.Provider value={contextValue}>
      {children}
    </EventContext.Provider>
  );
};
